import type { EmployeeRowDto } from '../api/hr-employee';
import type { QuotaRowDto } from '../api/hr-admin';

/**
 * 인사 통계 — **집계는 여기서만** 한다 (시안 A18, 2026-08-27).
 *
 * ── 왜 새 표도 새 라우트도 없나 ──────────────────────────────────────────
 * 시안이 요구하는 값(인원 구성 · 근속 · 입퇴사 · 연차 소진율)은 **인사 담당자가 이미 부르는
 * 응답 안에 전부 들어 있다** — 직원 명부(`listEmployees`) · 연차 현황(`listLeaveQuotas`) ·
 * 대표·부서장(`getOrgLeaders`, 팀→본부 지도) · 퇴사 기록(퇴사 처리 · 퇴직 발령).
 * 통계는 **새 사실을 만들지 않는다** — 있는 것을 세로로 세울 뿐이다. 그래서 표를 만들지도,
 * 집계 라우트를 늘리지도 않았다(인사 홈 `lib/hr-home.ts` 와 같은 판단).
 *
 * ── 셀 수 없는 것은 세지 않는다 ─────────────────────────────────────────
 * ★ **평균 연령은 못 센다.** `users.birthday` 는 연도 없는 `MM-DD` 라(migration 106) 나이를
 *   만들 길이 없다. 시안 A18 의 여섯 번째 지표가 그것인데, 그럴듯한 숫자를 지어내는 대신
 *   화면이 «지금 셀 수 없는 것» 으로 말한다.
 * ★ **지난 해는 지금 명부로 되짚는다.** 직원 명부에는 연도 파라미터가 없다 — 그 해 중간에
 *   나간 사람은 명부에 남아 있을 때만 셈에 든다. 인원 구성(부서·직위·근속)은 그래서
 *   **언제나 기준일 현재**이고, 연도는 입퇴사·연차에만 걸린다.
 *
 * ── 날짜를 밖에서 받는 이유 ──────────────────────────────────────────────
 * `new Date()` 를 안에서 부르면 테스트가 «오늘» 에 따라 흔들린다. 부르는 쪽이 기준일
 * (`YYYY-MM-DD`)을 넘기고, 비교는 **문자열 앞자리**로만 한다 — 시간대 연산이 없다
 * (서버 시간대는 KST 이고 hireDate·resignDate 는 날짜만 있는 값이다).
 */

/** 퇴사 한 건 — 퇴사 처리(`resignDate`)와 퇴직 발령(`effectiveDate`)이 같은 모양으로 들어온다. */
export interface ResignRecord {
  userId: string;
  /** `YYYY-MM-DD`. */
  date: string;
}

export type HrStatsGroupBy = 'team' | 'division';

export interface HrStatsInput {
  employees: readonly EmployeeRowDto[];
  quotas: readonly QuotaRowDto[];
  /** 팀 이름 → 본부(부서) 이름. 대표·부서장 응답에서 만든다. 비면 «본부 미지정» 으로 묶인다. */
  divisionByTeamName?: ReadonlyMap<string, string>;
  /** 퇴사 기록 — 없으면 퇴사 지표가 «기록 없음» 이 된다(0 으로 단정하지 않는다). */
  resigns?: readonly ResignRecord[];
  year: number;
  /** 기준일 `YYYY-MM-DD` — 근속의 «오늘». */
  asOf: string;
  groupBy: HrStatsGroupBy;
}

export interface HrTenureBucket {
  label: string;
  count: number;
}

export interface HrGroupRow {
  label: string;
  /** 재직자 수(기준일 현재). */
  headcount: number;
  /** 그 해 부여 일수 합. */
  total: number;
  used: number;
  remain: number;
  /** 0~100(소수 첫째). 부여가 0이면 0. */
  rate: number;
}

export interface HrPositionRow {
  label: string;
  count: number;
  /** 정렬용 — 같은 직위 안에서 가장 높은 레벨. 없으면 null(맨 뒤). */
  jobLevel: number | null;
  /** 재직자 대비 비율 0~100(소수 첫째). */
  share: number;
}

export interface HrMonthRow {
  /** 1~12. */
  month: number;
  hired: number;
  left: number;
}

export interface HrStats {
  year: number;
  asOf: string;
  headcount: {
    /** 재직(ACTIVE). 휴직·퇴사는 따로 센다. */
    active: number;
    onLeave: number;
    resigned: number;
    regular: number;
    contract: number;
    parttime: number;
    /** 고용형태가 비어 있는 재직자 — 연차·소정 계산이 갈리는 칸이라 따로 센다. */
    typeUnset: number;
  };
  tenure: {
    /** 평균 근속(년, 소수 첫째). 입사일 있는 재직자가 없으면 null. */
    avgYears: number | null;
    /** 그 평균이 몇 명을 근거로 하는가. */
    known: number;
    buckets: HrTenureBucket[];
  };
  hires: {
    /** 그 해 입사(명부에 남아 있는 사람 기준). */
    count: number;
    /** 기준일이 든 달의 입사. */
    thisMonth: number;
  };
  leavers: {
    /** 그 해 퇴사 — 기록(퇴사 처리·퇴직 발령)에서 센다. */
    count: number;
    /** 기록이 하나도 없으면 false — 화면이 «0명» 대신 «기록 없음» 을 말한다. */
    hasRecords: boolean;
    /** 이직률 0~100(소수 첫째) = 그 해 퇴사 ÷ (재직 + 그 해 퇴사). 기록이 없으면 null. */
    turnoverRate: number | null;
    /** 재직 상태가 «퇴사» 인 사람 — 날짜를 모르는 것까지 포함한 총계. */
    statusResigned: number;
  };
  monthly: HrMonthRow[];
  /** 부서(팀·본부)별 인원과 연차. 인원 많은 순. */
  groups: HrGroupRow[];
  /** 직위 구성 — 높은 직급이 먼저. */
  positions: HrPositionRow[];
  quota: {
    total: number;
    used: number;
    remain: number;
    rate: number;
    /** 연차가 아직 산정되지 않은 사람 수. */
    missing: number;
  };
  gaps: {
    missingHireDate: number;
    missingTeam: number;
  };
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * 팀이 없는 사람들의 묶음 이름 — **조직도와 같은 말**이어야 한다 (2026-08-31 · 사용자 결정).
 * 「미배정」은 «어디에도 없다»로 읽히지만, 팀이 없다는 것은 **회사에 바로 속한다**는 뜻이다.
 */
const UNASSIGNED = '회사 직속';
const NO_DIVISION = '본부 미지정';
const NO_POSITION = '직위 미지정';

/**
 * 근속 개월 — 서버 `leave-rules.ts` 의 `tenureMonths` 와 **같은 규칙**이다
 * (일(日)이 아직 안 지났으면 한 달 빼고, 음수는 0). 다만 `Date` 를 만들지 않고 문자열을
 * 가른다 — 브라우저 시간대가 하루를 밀어 근속이 사람마다 달라지지 않게.
 */
export function tenureMonthsOf(hireDate: string, asOf: string): number {
  const h = hireDate.split('-').map(Number);
  const a = asOf.split('-').map(Number);
  const hy = h[0] ?? 0, hm = h[1] ?? 0, hd = h[2] ?? 0;
  const ay = a[0] ?? 0, am = a[1] ?? 0, ad = a[2] ?? 0;
  if (!hy || !ay) return 0;
  let months = (ay - hy) * 12 + (am - hm);
  if (ad < hd) months -= 1;
  return months > 0 ? months : 0;
}

/** 근속 구간 — 시안 A18 의 다섯 칸. 경계는 «미만/이상» 으로 겹치지 않는다. */
const TENURE_BUCKETS: Array<{ label: string; upto: number }> = [
  { label: '1년 미만', upto: 12 },
  { label: '1~3년', upto: 36 },
  { label: '3~5년', upto: 60 },
  { label: '5~10년', upto: 120 },
  { label: '10년+', upto: Infinity },
];

/**
 * 그 해의 기준일 — 서버 `leave-admin.routes.ts` 의 `asOfForYear` 와 같은 규칙이다.
 * 올해면 오늘, 지난 해면 그 해 12/31, 앞으로 올 해면 1/1. 연차 현황이 그 기준으로 오므로
 * 화면의 근속도 같은 날을 봐야 두 숫자가 서로 어긋나지 않는다.
 */
export function asOfForYear(year: number, today: string): string {
  const thisYear = Number(today.slice(0, 4));
  if (year === thisYear) return today;
  if (year < thisYear) return `${year}-12-31`;
  return `${year}-01-01`;
}

/** `YYYY-MM-DD` — 부르는 쪽이 «오늘» 을 정한다(테스트는 고정 값을 넘긴다). */
export function dateOnlyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function buildHrStats(input: HrStatsInput): HrStats {
  const { employees, quotas, year, asOf, groupBy } = input;
  const divisionByTeamName = input.divisionByTeamName ?? new Map<string, string>();
  const yearPrefix = `${year}-`;

  const active = employees.filter((e) => e.employmentStatus === 'ACTIVE');

  // ── 부서 묶음 이름 — 팀 이름 하나로 두 축을 만든다(연차 현황에는 teamId 가 없다). ──
  const groupKeyOf = (teamName: string | null): string => {
    if (groupBy === 'team') return teamName ?? UNASSIGNED;
    if (!teamName) return UNASSIGNED;
    return divisionByTeamName.get(teamName) ?? NO_DIVISION;
  };

  const groups = new Map<string, { headcount: number; total: number; used: number }>();
  const bump = (key: string) => {
    const cur = groups.get(key) ?? { headcount: 0, total: 0, used: 0 };
    groups.set(key, cur);
    return cur;
  };
  for (const e of active) bump(groupKeyOf(e.teamName)).headcount += 1;

  // ── 연차 — 합계와 부서별을 한 번에 센다. ──
  let total = 0;
  let used = 0;
  let missing = 0;
  for (const q of quotas) {
    if (q.annualTotal === null) missing += 1;
    total += q.annualTotal ?? 0;
    used += q.usedDays;
    const cur = bump(groupKeyOf(q.teamName));
    cur.total += q.annualTotal ?? 0;
    cur.used += q.usedDays;
  }

  // ── 근속 ──
  const buckets = TENURE_BUCKETS.map((b) => ({ label: b.label, count: 0 }));
  let monthsSum = 0;
  let known = 0;
  for (const e of active) {
    if (!e.hireDate) continue;
    const months = tenureMonthsOf(e.hireDate, asOf);
    known += 1;
    monthsSum += months;
    const idx = TENURE_BUCKETS.findIndex((b) => months < b.upto);
    const slot = buckets[idx === -1 ? buckets.length - 1 : idx];
    if (slot) slot.count += 1;
  }

  // ── 입 · 퇴사 ── 퇴사는 사람 하나가 두 기록(퇴사 처리 + 퇴직 발령)을 가질 수 있어 **먼저 접는다**.
  const resignByUser = new Map<string, string>();
  for (const r of input.resigns ?? []) {
    const prev = resignByUser.get(r.userId);
    if (!prev || r.date < prev) resignByUser.set(r.userId, r.date);
  }
  const resignDates = [...resignByUser.values()];
  const leftThisYear = resignDates.filter((d) => d.startsWith(yearPrefix)).length;
  const hiredThisYear = employees.filter((e) => (e.hireDate ?? '').startsWith(yearPrefix));

  const monthly: HrMonthRow[] = Array.from({ length: 12 }, (_, i) => {
    const prefix = `${year}-${String(i + 1).padStart(2, '0')}`;
    return {
      month: i + 1,
      hired: employees.filter((e) => (e.hireDate ?? '').startsWith(prefix)).length,
      left: resignDates.filter((d) => d.startsWith(prefix)).length,
    };
  });

  // ── 직위 ── 이름은 회사마다 다르고 비어 있을 수도 있다. 정렬은 **레벨**로 한다(문자열로는
  //   "선임" 이 "책임" 보다 위인지 알 수 없다 — `users.jobLevel` 이 그래서 있다).
  const byPosition = new Map<string, { count: number; jobLevel: number | null }>();
  for (const e of active) {
    const key = e.position?.trim() || NO_POSITION;
    const cur = byPosition.get(key) ?? { count: 0, jobLevel: null };
    cur.count += 1;
    if (e.jobLevel !== null && (cur.jobLevel === null || e.jobLevel > cur.jobLevel)) cur.jobLevel = e.jobLevel;
    byPosition.set(key, cur);
  }
  const positions: HrPositionRow[] = [...byPosition.entries()]
    .map(([label, v]) => ({
      label,
      count: v.count,
      jobLevel: v.jobLevel,
      share: active.length > 0 ? round1((v.count / active.length) * 100) : 0,
    }))
    .sort((a, b) => {
      if (a.jobLevel !== b.jobLevel) {
        if (a.jobLevel === null) return 1;
        if (b.jobLevel === null) return -1;
        return b.jobLevel - a.jobLevel;
      }
      return b.count - a.count;
    });

  /**
   * «회사 직속» · «본부 미지정»은 인원이 많아도 **맨 뒤로** 보낸다 — 조직의 모양을 읽는
   * 자리에서 그것들이 앞에 서면 부서 순위가 아니라 그 두 줄이 먼저 읽힌다.
   *
   * ★ 2026-08-31 — 「미배정」이 「회사 직속」이 되면서 **뜻이 달라졌다.** 「본부 미지정」은
   *   여전히 «채워지지 않은 칸»이지만, 「회사 직속」은 **제자리**다(대표·시스템 계정).
   *   그래도 맨 뒤에 두는 이유는 «빈 칸이라서»가 아니라 **부서·팀이 아닌 묶음**이라
   *   부서 순위와 나란히 세울 것이 아니기 때문이다.
   */
  const isGapLabel = (label: string) => label === UNASSIGNED || label === NO_DIVISION;
  const groupRows: HrGroupRow[] = [...groups.entries()]
    .map(([label, v]) => ({
      label,
      headcount: v.headcount,
      total: round1(v.total),
      used: round1(v.used),
      remain: round1(v.total - v.used),
      rate: v.total > 0 ? round1((v.used / v.total) * 100) : 0,
    }))
    .sort((a, b) =>
      Number(isGapLabel(a.label)) - Number(isGapLabel(b.label))
      || b.headcount - a.headcount
      || a.label.localeCompare(b.label));

  return {
    year,
    asOf,
    headcount: {
      active: active.length,
      onLeave: employees.filter((e) => e.employmentStatus === 'LEAVE').length,
      resigned: employees.filter((e) => e.employmentStatus === 'RESIGNED').length,
      regular: active.filter((e) => e.employmentType === 'REGULAR').length,
      contract: active.filter((e) => e.employmentType === 'CONTRACT').length,
      parttime: active.filter((e) => e.employmentType === 'PARTTIME').length,
      typeUnset: active.filter((e) => !e.employmentType).length,
    },
    tenure: {
      avgYears: known > 0 ? round1(monthsSum / known / 12) : null,
      known,
      buckets,
    },
    hires: {
      count: hiredThisYear.length,
      thisMonth: employees.filter((e) => (e.hireDate ?? '').startsWith(asOf.slice(0, 7))).length,
    },
    leavers: {
      count: leftThisYear,
      hasRecords: resignDates.length > 0,
      turnoverRate:
        resignDates.length === 0 || active.length + leftThisYear === 0
          ? null
          : round1((leftThisYear / (active.length + leftThisYear)) * 100),
      statusResigned: employees.filter((e) => e.employmentStatus === 'RESIGNED').length,
    },
    monthly,
    groups: groupRows,
    positions,
    quota: {
      total: round1(total),
      used: round1(used),
      remain: round1(total - used),
      rate: total > 0 ? round1((used / total) * 100) : 0,
      missing,
    },
    gaps: {
      missingHireDate: active.filter((e) => !e.hireDate).length,
      missingTeam: active.filter((e) => !e.teamId).length,
    },
  };
}

/**
 * 부서별 표를 그대로 내려받는 자리(시안 A18 «엑셀») — **보이는 표와 같은 값**이어야 한다.
 *
 * ★ 엑셀이 한글을 깨뜨리지 않게 BOM 을 앞에 둔다. 줄 끝은 CRLF(윈도 엑셀의 기본 기대값).
 * ★ 값에 쉼표·따옴표·줄바꿈이 있으면 큰따옴표로 감싸고 안의 따옴표는 두 번 적는다.
 */
export function hrStatsCsv(stats: HrStats, groupLabel: string): string {
  const cell = (v: string | number): string => {
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows: Array<Array<string | number>> = [
    [groupLabel, '인원', '부여(일)', '사용(일)', '잔여(일)', '소진율(%)'],
    ...stats.groups.map((g) => [g.label, g.headcount, g.total, g.used, g.remain, g.rate]),
    ['합계', stats.headcount.active, stats.quota.total, stats.quota.used, stats.quota.remain, stats.quota.rate],
  ];
  return `﻿${rows.map((r) => r.map(cell).join(',')).join('\r\n')}\r\n`;
}
